/**
 * ChatPage - Full-page chat interface for the Dynatron AI chatbot.
 * Supports text messages, file attachments (clipboard paste + file picker),
 * and streaming responses.
 */
import { useRef, useEffect, useState, useCallback, type KeyboardEvent } from 'react';
import { clsx } from 'clsx';
import { MainLayout } from '@/components/layout';
import { useChatSession } from '@/hooks/ChatSessionProvider';
import { useAccounts } from '@/hooks';
import { AccountSelector } from '@/components/dashboard';
import { ChatMessage } from '@/components/chat/ChatMessage';
import { ActivityIndicator } from '@/components/chat/ActivityIndicator';
import { TradeConfirmation } from '@/components/chat/TradeConfirmation';
import { AgentSelector } from '@/components/chat/AgentSelector';
import type { ChatAttachment, AllowedMimeType } from '@/types/chat';
import {
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '@/types/chat';

/** Human-readable file size */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Generate a unique attachment ID */
function generateAttachmentId(): string {
  return `att_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Check if a MIME type is allowed */
function isAllowedMimeType(type: string): type is AllowedMimeType {
  return (ALLOWED_MIME_TYPES as readonly string[]).includes(type);
}

/** Read a File as base64 string (without the data URL prefix) */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:...;base64," prefix
      const base64 = result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/** Validate and process a File into a ChatAttachment */
async function processFile(file: File): Promise<{ attachment?: ChatAttachment; error?: string }> {
  if (!isAllowedMimeType(file.type)) {
    return { error: `Unsupported file type: ${file.type || 'unknown'}. Allowed: PDF, JPEG, PNG, GIF, WebP` };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { error: `File too large: ${formatFileSize(file.size)}. Maximum: ${formatFileSize(MAX_FILE_SIZE_BYTES)}` };
  }
  if (file.size === 0) {
    return { error: 'File is empty' };
  }

  const base64 = await fileToBase64(file);
  const isImage = file.type.startsWith('image/');

  const attachment: ChatAttachment = {
    id: generateAttachmentId(),
    name: file.name || `attachment.${file.type.split('/')[1]}`,
    mimeType: file.type as AllowedMimeType,
    size: file.size,
    base64,
    previewUrl: isImage ? URL.createObjectURL(file) : undefined,
  };

  return { attachment };
}

export function ChatPage() {
  const {
    messages,
    isStreaming,
    pendingTrade,
    error,
    sendMessage,
    confirmTrade,
    cancelTrade,
    newConversation,
    selectedAgent,
    setSelectedAgent,
  } = useChatSession();

  const { accounts, isLoading: accountsLoading } = useAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // Auto-select first account on load
  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0 && !accountsLoading) {
      const firstAccount = accounts[0];
      if (firstAccount) {
        setSelectedAccountId(firstAccount.account_id);
      }
    }
  }, [accounts, accountsLoading, selectedAccountId]);

  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);

  // Focus textarea on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      attachments.forEach((att) => {
        if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-clear attachment error after 5 seconds
  useEffect(() => {
    if (!attachmentError) return;
    const timer = setTimeout(() => setAttachmentError(null), 5000);
    return () => clearTimeout(timer);
  }, [attachmentError]);

  // Auto-scroll behavior
  const userSentRef = useRef(false);

  const isNearBottom = useCallback((): boolean => {
    const container = messageListRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= 50;
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollButton(false);
  }, []);

  // Mark that the user just sent a message so we always scroll
  useEffect(() => {
    if (isStreaming) {
      userSentRef.current = true;
    }
  }, [isStreaming]);

  useEffect(() => {
    if (userSentRef.current || isNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowScrollButton(false);
    } else {
      setShowScrollButton(true);
    }

    // Reset the flag once streaming is done
    if (!isStreaming) {
      userSentRef.current = false;
    }
  }, [messages, isStreaming, isNearBottom]);

  const handleScroll = useCallback(() => {
    setShowScrollButton(!isNearBottom());
  }, [isNearBottom]);

  // --- File handling ---

  const addFiles = useCallback(async (files: File[]) => {
    setAttachmentError(null);

    for (const file of files) {
      if (attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
        setAttachmentError(`Maximum ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message`);
        break;
      }

      const { attachment, error: fileError } = await processFile(file);
      if (fileError) {
        setAttachmentError(fileError);
        continue;
      }
      if (attachment) {
        setAttachments((prev) => {
          if (prev.length >= MAX_ATTACHMENTS_PER_MESSAGE) return prev;
          return [...prev, attachment];
        });
      }
    }
  }, [attachments.length]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const handleFilePickerClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  }, [addFiles]);

  // --- Paste handler ---

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const fileItems = items.filter((item) => item.kind === 'file');

    if (fileItems.length === 0) return; // Let default paste behavior handle text

    e.preventDefault();
    const files = fileItems
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);

    if (files.length > 0) {
      addFiles(files);
    }
  }, [addFiles]);

  // --- Send handling ---

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    const hasContent = trimmed || attachments.length > 0;
    if (!hasContent || isStreaming) return;

    setInputValue('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    const currentAttachments = attachments.length > 0 ? [...attachments] : undefined;
    setAttachments([]);
    setAttachmentError(null);

    sendMessage(trimmed, currentAttachments, selectedAccountId || undefined);
  }, [inputValue, attachments, isStreaming, sendMessage, selectedAccountId]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const isTradeExpired = pendingTrade
    ? new Date(pendingTrade.expiresAt).getTime() <= Date.now()
    : false;

  const canSend = (inputValue.trim() || attachments.length > 0) && !isStreaming;

  return (
    <MainLayout>
      <div className="flex h-[calc(100vh-6rem)] flex-col lg:h-[calc(100vh-7rem)]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-dark-100">Chat</h1>
            <AccountSelector
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelect={setSelectedAccountId}
              isLoading={accountsLoading}
            />
          </div>
          <button
            type="button"
            onClick={newConversation}
            className="flex items-center gap-2 rounded-lg border border-dark-700 bg-dark-800/50 px-3 py-2 text-sm text-dark-300 transition-colors hover:bg-dark-700 hover:text-dark-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Conversation
          </button>
        </div>

        {/* Message area */}
        <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-dark-700/50 bg-dark-900/50">
          {/* Messages */}
          <div
            ref={messageListRef}
            onScroll={handleScroll}
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
            className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 space-y-4"
          >
            {messages.length === 0 && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-dark-800">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-6 w-6 text-dark-400" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-dark-400">
                    Ask me about your positions, portfolio performance, or enter a trade.
                  </p>
                  <p className="mt-1 text-xs text-dark-500">
                    You can paste or attach images and PDFs for analysis.
                  </p>
                </div>
              </div>
            )}

            {messages.map((msg) => {
              // Skip empty assistant messages (e.g., when trade confirmation replaces text)
              if (msg.role === 'assistant' && !msg.content && !msg.isStreaming && !msg.tradeConfirmation) return null;

              // Render resolved trade cards inline in the message flow
              if (msg.tradeConfirmation) {
                return (
                  <div key={msg.id} className="flex w-full justify-start">
                    <div className="w-1/2 min-w-[280px] max-w-sm">
                      <TradeConfirmation
                        trade={msg.tradeConfirmation}
                        onConfirm={() => {}}
                        onCancel={() => {}}
                        isExpired={false}
                      />
                    </div>
                  </div>
                );
              }

              // Assistant messages are grouped with their Activity_Indicator,
              // rendered adjacent to (directly below) the message bubble and
              // left-aligned to match it. The indicator renders nothing when the
              // message has no activities.
              if (msg.role === 'assistant') {
                return (
                  <div key={msg.id} className="space-y-1">
                    <ChatMessage message={msg} isStreaming={msg.isStreaming} />
                    <div className="flex w-full justify-start">
                      <div className="max-w-[85%]">
                        <ActivityIndicator message={msg} />
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isStreaming={msg.isStreaming}
                />
              );
            })}

            {pendingTrade && (
              <div className="flex w-full justify-start">
                <div className="w-1/2 min-w-[280px] max-w-sm">
                  <TradeConfirmation
                    trade={pendingTrade}
                    onConfirm={confirmTrade}
                    onCancel={cancelTrade}
                    isExpired={isTradeExpired}
                  />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom */}
          {showScrollButton && (
            <div className="relative">
              <button
                type="button"
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
                className="absolute -top-10 left-1/2 -translate-x-1/2 rounded-full bg-dark-700/90 px-3 py-1.5 text-xs text-dark-300 shadow-md hover:bg-dark-600 hover:text-dark-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-all flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                <span>New messages</span>
              </button>
            </div>
          )}

          {/* Error display */}
          {(error || attachmentError) && (
            <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-error-700/50 bg-error-900/30 px-3 py-2 text-xs text-error-300 sm:mx-6">
              <svg className="h-4 w-4 flex-shrink-0 text-error-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className="flex-1">{attachmentError || error}</span>
            </div>
          )}

          {/* Attachment preview strip */}
          {attachments.length > 0 && (
            <div className="mx-4 mb-2 flex flex-wrap gap-2 sm:mx-6">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="group relative flex items-center gap-2 rounded-lg border border-dark-600/50 bg-dark-800/80 px-2.5 py-1.5"
                >
                  {/* Thumbnail or icon */}
                  {att.previewUrl ? (
                    <img
                      src={att.previewUrl}
                      alt={att.name}
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-dark-700">
                      <svg className="h-4 w-4 text-dark-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  {/* File info */}
                  <div className="flex flex-col">
                    <span className="max-w-[120px] truncate text-xs text-dark-200">{att.name}</span>
                    <span className="text-[10px] text-dark-500">{formatFileSize(att.size)}</span>
                  </div>
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => removeAttachment(att.id)}
                    aria-label={`Remove ${att.name}`}
                    className="ml-1 flex h-5 w-5 items-center justify-center rounded-full text-dark-500 transition-colors hover:bg-dark-600 hover:text-dark-200"
                  >
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-dark-700/50 px-4 py-3 sm:px-6">
            <AgentSelector
              selectedAgent={selectedAgent}
              onAgentChange={setSelectedAgent}
              disabled={isStreaming}
            />
            <div className="flex items-end gap-2">
              {/* Paperclip attachment button */}
              <button
                type="button"
                onClick={handleFilePickerClick}
                disabled={isStreaming || attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                aria-label="Attach file"
                title="Attach file (PDF, JPEG, PNG, GIF, WebP — max 10 MB)"
                className={clsx(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900',
                  isStreaming || attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
                    ? 'cursor-not-allowed text-dark-600'
                    : 'text-dark-400 hover:bg-dark-700 hover:text-dark-200'
                )}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              </button>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleFileInputChange}
                className="hidden"
                aria-hidden="true"
              />

              {/* Text input */}
              <textarea
                ref={inputRef}
                role="textbox"
                aria-label="Message input"
                placeholder={isStreaming ? 'Waiting for response...' : 'Type a message or paste an image...'}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={isStreaming}
                rows={1}
                className={clsx(
                  'flex-1 resize-none rounded-lg border border-dark-600/50 bg-dark-800/80 px-4 py-2.5 text-sm text-dark-100 placeholder-dark-500',
                  'focus:border-primary-500/50 focus:outline-none focus:ring-1 focus:ring-primary-500/30',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  'transition-colors'
                )}
              />

              {/* Send button */}
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                aria-label="Send message"
                className={clsx(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900',
                  !canSend
                    ? 'cursor-not-allowed bg-dark-700 text-dark-500'
                    : 'bg-primary-600 text-white hover:bg-primary-500 active:bg-primary-700'
                )}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
